// Isometric helpers (30° axes) matching the SVG grid orientation
const debug = (typeof window !== 'undefined' && window.DEBUG) ? window.DEBUG : false;
export const u = {x: 0.8660254037844386, y: 0.5};
export const v = {x: 0.8660254037844386, y: -0.5};
export const isometricProjection = 1.154700538 ;

/*
	u = ⤰, v = ⤯ :
	position = Xu + Yv
	x = X*u.x + Y*v.x
	y = X*u.y + Y*v.y
*/

/**
 * Loads an image and returns its aspect ratio (height/width)
 * @param {string} imgPath - Path to the image file
 * @returns {Promise<number>} The aspect ratio (height/width)
 */
export function getImageAspectRatio(imgPath) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			const aspectRatio = img.naturalHeight / img.naturalWidth;
			resolve(aspectRatio);
		};
		img.onerror = () => reject(new Error(`Failed to load image: ${imgPath}`));
		img.src = imgPath;
	});
}

/**
 * Calculates height from width and aspect ratio
 * @param {number} w - Width in pixels
 * @param {number} aspectRatio - Image aspect ratio (height/width)
 * @returns {number} Height in pixels
 */
export function sizeToPixels(w, aspectRatio) {
	return w * aspectRatio;
}

// Converts grid coordinates (X, Y) to screen pixels from a bottom-left origin
// If aspectRatio is provided, height is calculated from width and aspect ratio instead of the footprint projection
export function gridToPixels(X, Y, W, L, cell, aspectRatio = null) {
	if (debug) console.log(`->object : W = ${W}, L = ${L}`);

	// Calculate width first
	const w = ((W * u.x * cell) + (L * v.x * cell))*isometricProjection; // width

	// Calculate height: use aspect ratio if provided, otherwise fall back to the footprint projection
	let h;
	if (aspectRatio !== null && aspectRatio !== undefined) {
		h = sizeToPixels(w, aspectRatio);
	} else {
		h = ((W * u.y * cell) - (L * v.y * cell))*isometricProjection; // height
	}
	
	// Calculate x position
	const x = ((X * u.x * cell) + (Y * v.x * cell) - (W * u.x * cell))*isometricProjection; //left
	
	// Calculate y position using the calculated height
	const y = ((X * u.y * cell) + (Y * v.y * cell))*isometricProjection - h; // top
	
	if (debug) {
		console.log(`->coordinates : x = ${x}, y = ${y}`);
		console.log(`->size : w = ${w}, h = ${h}`);
	}
	return { x, y, w, h };
}

// Inverse of gridToPixels' linear part: turns a screen-space drag delta into
// a grid-space (X, Y) delta. Solves [dxPx;dyPx] = cell*isometricProjection * [[u.x,v.x],[u.y,v.y]] * [dX;dY]
// via Cramer's rule.
export function pixelsToGridDelta(dxPx, dyPx, cell) {
	const k = cell * isometricProjection;
	const dx = dxPx / k;
	const dy = dyPx / k;
	const det = u.x * v.y - v.x * u.y;
	const dX = (v.y * dx - v.x * dy) / det;
	const dY = (u.x * dy - u.y * dx) / det;
	return { dX, dY };
}

// Dragging the "W" edge moves the sprite's left edge (screen x) but leaves its
// right edge fixed (see gridToPixels: only W appears in the `x` term), so a
// horizontal screen delta on that edge maps to a pure W change.
export function pixelsToWidthDelta(dxPx, cell) {
	return -dxPx / (u.x * cell * isometricProjection);
}

// Dragging the "L" edge moves the sprite's right edge but leaves the left edge
// fixed (L never appears in the `x` term), so it maps to a pure L change.
export function pixelsToLengthDelta(dxPx, cell) {
	return dxPx / (v.x * cell * isometricProjection);
}


